import { useCallback } from "react"

import { useAppDispatch, useAppSelector } from "../../../store/storeHooks"
import { useMerchant } from "../../../merchants/merchantHooks"

import { PriceListSelectionScope, SelectedPriceListId, setSelectedPriceList } from "./priceListSelectionSlice"

/**
 * Per-merchant, per-area "currently selected price list", kept in the store so the
 * selection survives unmount/navigation (e.g. returning from create/edit product group)
 * and can be reused across an area (the Products list and the Create product form share
 * the `products` scope). The stored value is scoped by merchant id, so switching merchant
 * starts from an empty selection.
 */
export function useSelectedPriceList(
    scope: PriceListSelectionScope,
): readonly [SelectedPriceListId, (priceListId: SelectedPriceListId) => void] {
    const dispatch = useAppDispatch()
    const { merchantId } = useMerchant()
    const stored = useAppSelector((state) => state.priceListSelection)

    const selectedPriceListId = stored.merchantId === merchantId ? stored.selectedByScope[scope] : null

    const setSelectedPriceListId = useCallback(
        (priceListId: SelectedPriceListId) => {
            dispatch(setSelectedPriceList({ merchantId, scope, priceListId }))
        },
        [dispatch, merchantId, scope],
    )

    return [selectedPriceListId, setSelectedPriceListId] as const
}
